"use client";

import { ArrowRight, Clock, Search, Users, Zap } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { FadeInUp, StaggerItem } from "@/components/ui/motion";
import { useIsMobile } from "@/hooks/use-mobile";
import { FinalCTA } from "../components/FinalCTA";
import { type Template, templateCategories, templates } from "../templatesData";

const TemplatesPage = () => {
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(
    null,
  );
  const navigate = useRouter();

  const filteredTemplates = templates.filter((template) => {
    const matchesCategory =
      activeCategory === "all" || template.category === activeCategory;
    const matchesSearch =
      template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleUseTemplate = (templateId: string) => {
    setSelectedTemplate(null);
    navigate.push(`/register?template=${templateId}`);
  };
  const isMobile = useIsMobile();

  return (
    <div className="min-h-screen bg-background">
      <main className="pt-16">
        {/* Hero */}
        <section className="py-16 lg:py-24 relative overflow-hidden">
          {/* Enhanced background orbs with better visibility */}
          <div className="absolute top-10 left-1/4 w-[600px] h-[600px] bg-primary/12 rounded-full blur-[140px] pointer-events-none" />
          <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-accent/15 rounded-full blur-[120px] pointer-events-none" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-primary/5 rounded-full blur-[100px] pointer-events-none" />

          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
            <FadeInUp className="max-w-3xl mx-auto text-center mb-12">
              <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-4">
                Start with a <span className="gradient-text">template</span>
              </h1>
              <p className="text-lg text-muted-foreground">
                Pre-built workflows to get you running in minutes.
              </p>
            </FadeInUp>

            <FadeInUp delay={0.1} className="max-w-2xl mx-auto">
              <div className="relative mb-6">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  placeholder="Search templates..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 h-12 bg-card border-border"
                />
              </div>

              <div className="flex flex-wrap justify-center gap-2">
                {templateCategories.map((category) => (
                  <Button
                    key={category.id}
                    variant={
                      activeCategory === category.id ? "default" : "outline"
                    }
                    size="sm"
                    onClick={() => setActiveCategory(category.id)}
                    className="rounded-full"
                  >
                    {category.label}
                  </Button>
                ))}
              </div>
            </FadeInUp>
          </div>
        </section>

        {/* Templates Grid */}
        <section className="pb-20 section-feature relative">
          {/* Decorative grid overlay */}
          <div className="absolute inset-0 pattern-dots opacity-30 pointer-events-none" />

          <div className="container mx-auto px-4 pt-4 sm:px-6 lg:px-8 relative">
            <AnimatePresence mode="popLayout">
              <motion.div
                layout
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                whileHover={isMobile ? {} : { y: -2 }}
                transition={{ duration: 0.2 }}
                className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
              >
                {filteredTemplates.map((template) => {
                  const IconComponent = template.nodes[0].icon;
                  return (
                    <StaggerItem key={template.id}>
                      <motion.div
                        whileHover={{ y: -4 }}
                        onClick={() => setSelectedTemplate(template)}
                        className="group h-full card-interactive p-6 cursor-pointer"
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
                            <IconComponent className="w-6 h-6 text-primary" />
                          </div>
                          {template.popular && (
                            <Badge variant="secondary" className="text-xs">
                              Popular
                            </Badge>
                          )}
                        </div>

                        <h3 className="text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                          {template.name}
                        </h3>
                        <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                          {template.outcome}
                        </p>

                        <div className="flex items-center justify-between pt-4 border-t border-border">
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            {template.uses && (
                              <span className="flex items-center gap-1">
                                <Users className="w-3.5 h-3.5" />
                                {template.uses}
                              </span>
                            )}
                            {template.time && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-3.5 h-3.5" />
                                {template.time}
                              </span>
                            )}
                          </div>
                          <ArrowRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </motion.div>
                    </StaggerItem>
                  );
                })}
              </motion.div>
            </AnimatePresence>

            {filteredTemplates.length === 0 && (
              <div className="text-center py-16">
                <p className="text-muted-foreground">No templates found.</p>
              </div>
            )}
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 section-gradient border-t border-border/50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <FadeInUp>
              <h2 className="text-2xl font-bold text-foreground mb-4">
                Don't see what you need?
              </h2>
              <p className="text-muted-foreground mb-6">
                Start from scratch and build exactly what you're looking for.
              </p>
              <Button size="lg" onClick={() => navigate.push("/register")}>
                <Zap className="w-4 h-4 mr-2" />
                Start building
              </Button>
            </FadeInUp>
          </div>
        </section>

        <FinalCTA />
      </main>

      <Dialog
        open={!!selectedTemplate}
        onOpenChange={() => setSelectedTemplate(null)}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{selectedTemplate?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 pt-2">
            <div className="flex items-center justify-center gap-3 py-4 px-4 rounded-lg bg-muted/50">
              {selectedTemplate?.nodes.map((node, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <div className="flex flex-col items-center gap-1.5">
                    <div className="w-10 h-10 rounded-lg bg-card border border-border flex items-center justify-center">
                      <node.icon className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <span className="text-[10px] text-muted-foreground font-medium">
                      {node.label}
                    </span>
                  </div>
                  {idx < (selectedTemplate?.nodes.length ?? 0) - 1 && (
                    <ArrowRight className="w-4 h-4 text-muted-foreground/40 mb-5" />
                  )}
                </div>
              ))}
            </div>
            <div>
              <h4 className="text-xs font-semibold text-foreground uppercase tracking-wider mb-2">
                What it does
              </h4>
              <p className="text-sm text-muted-foreground">
                {selectedTemplate?.description}
              </p>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-foreground uppercase tracking-wider mb-2">
                When to use
              </h4>
              <p className="text-sm text-muted-foreground">
                {selectedTemplate?.whenToUse}
              </p>
            </div>
            <Button
              className="w-full"
              onClick={() =>
                selectedTemplate && handleUseTemplate(selectedTemplate.id)
              }
            >
              Use this template
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TemplatesPage;
